import { useState } from 'react';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { CalendarCheck2, Pencil, Trash2 } from 'lucide-react';
import {
  CreateOrgAcademicYearData,
  OrgAcademicYearActionResult,
  OrgAcademicYearRow,
  OrgAcademicYearsApi,
  UpdateOrgAcademicYearData,
} from '@/services/org-academic-years.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Field, Input } from '@/components/ui/Input';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Badge } from '@/components/ui/Badge';
import { DataTable, useDataTable } from '@/components/data-table';

const columnHelper = createColumnHelper<OrgAcademicYearRow>();

function errMsg(e: unknown): string {
  const anyE = e as any;
  return anyE?.response?.data?.error?.message ?? anyE?.response?.data?.message ?? anyE?.message ?? 'Something went wrong';
}

function summarize(results: OrgAcademicYearActionResult[]): string {
  const counts = new Map<string, number>();
  for (const r of results) counts.set(r.status, (counts.get(r.status) ?? 0) + 1);
  return [...counts.entries()].map(([status, n]) => `${n} ${status.replace('_', ' ')}`).join(', ');
}

interface FormState {
  oldName: string | null;
  name: string;
  startDate: string;
  endDate: string;
}

const BLANK_FORM: FormState = { oldName: null, name: '', startDate: '', endDate: '' };

/**
 * Org-tier Session Years — real legacy's `SessionYearController` is
 * genuinely Super-Admin-only with NO school-select field anywhere: create
 * broadcasts one name/start/end to every school at once (`School::all()`
 * loop), and edit/delete/set-current all resolve "which rows" by matching
 * NAME across every school, not by picking one school first. Mirrored
 * here — see OrgReportsService.academicYears()'s doc comment for how this
 * adapts to schema-per-tenant (no shared table to update in one query, so
 * each action loops every school and reports a per-school result).
 */
export function OrgSessionYearsPage() {
  const qc = useQueryClient();
  const dt = useDataTable({ defaultLimit: 20 });

  const [form, setForm] = useState<FormState>(BLANK_FORM);
  const [formError, setFormError] = useState<string | undefined>();
  const [resultSummary, setResultSummary] = useState<string | undefined>();
  const [deleting, setDeleting] = useState<OrgAcademicYearRow | null>(null);
  const [deleteError, setDeleteError] = useState<string | undefined>();

  const isEditing = !!form.oldName;

  const { data, isFetching } = useQuery({
    queryKey: ['org-academic-years', dt.params],
    queryFn: () => OrgAcademicYearsApi.list({ page: dt.params.page, limit: dt.params.limit }),
    placeholderData: keepPreviousData,
  });

  const save = useMutation({
    mutationFn: () => {
      if (isEditing) {
        const payload: UpdateOrgAcademicYearData = {
          oldName: form.oldName!,
          name: form.name,
          startDate: form.startDate,
          endDate: form.endDate,
        };
        return OrgAcademicYearsApi.update(payload);
      }
      const payload: CreateOrgAcademicYearData = { name: form.name, startDate: form.startDate, endDate: form.endDate };
      return OrgAcademicYearsApi.create(payload);
    },
    onSuccess: (results) => {
      setForm(BLANK_FORM);
      setFormError(undefined);
      setResultSummary(summarize(results));
      qc.invalidateQueries({ queryKey: ['org-academic-years'] });
    },
    onError: (e) => setFormError(errMsg(e)),
  });

  const setCurrent = useMutation({
    mutationFn: (name: string) => OrgAcademicYearsApi.setCurrent(name),
    onSuccess: (results) => {
      setResultSummary(summarize(results));
      qc.invalidateQueries({ queryKey: ['org-academic-years'] });
    },
  });

  const remove = useMutation({
    mutationFn: (name: string) => OrgAcademicYearsApi.remove(name),
    onSuccess: (results) => {
      setDeleting(null);
      setResultSummary(summarize(results));
      qc.invalidateQueries({ queryKey: ['org-academic-years'] });
    },
    onError: (e) => setDeleteError(errMsg(e)),
  });

  function startEdit(r: OrgAcademicYearRow) {
    setFormError(undefined);
    setResultSummary(undefined);
    setForm({ oldName: r.name, name: r.name, startDate: r.startDate.slice(0, 10), endDate: r.endDate.slice(0, 10) });
  }

  const COLUMNS = [
    columnHelper.display({
      id: 'no',
      header: 'No.',
      enableSorting: false,
      cell: (info) => (
        <span className="text-slate-400">{dt.pagination.pageIndex * dt.pagination.pageSize + info.row.index + 1}</span>
      ),
    }),
    columnHelper.accessor('name', { header: 'Name' }),
    columnHelper.display({ id: 'startDate', header: 'Start Date', cell: (ctx) => ctx.row.original.startDate.slice(0, 10) }),
    columnHelper.display({ id: 'endDate', header: 'End Date', cell: (ctx) => ctx.row.original.endDate.slice(0, 10) }),
    columnHelper.display({
      id: 'schools',
      header: 'Schools',
      cell: (ctx) => (
        <Badge tone="slate">
          {ctx.row.original.schoolCount}/{ctx.row.original.totalSchools}
        </Badge>
      ),
    }),
    columnHelper.display({
      id: 'current',
      header: 'Current',
      cell: (ctx) =>
        ctx.row.original.currentInCount > 0 ? (
          <Badge tone="blue">Current in {ctx.row.original.currentInCount}</Badge>
        ) : (
          <span className="text-slate-400">—</span>
        ),
    }),
    columnHelper.display({
      id: 'action',
      header: 'Action',
      enableSorting: false,
      cell: (ctx) => (
        <span className="inline-flex items-center gap-2">
          <button
            type="button"
            title="Set as current"
            className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-brand-700 disabled:opacity-50"
            disabled={setCurrent.isPending}
            onClick={() => setCurrent.mutate(ctx.row.original.name)}
          >
            <CalendarCheck2 className="h-4 w-4" />
          </button>
          <button
            type="button"
            title="Edit"
            className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-brand-700"
            onClick={() => startEdit(ctx.row.original)}
          >
            <Pencil className="h-4 w-4" />
          </button>
          <button
            type="button"
            title="Delete"
            className="rounded p-1 text-slate-500 hover:bg-red-50 hover:text-red-600"
            onClick={() => {
              setDeleteError(undefined);
              setDeleting(ctx.row.original);
            }}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </span>
      ),
    }),
  ];

  const canSubmit = !!form.name.trim() && !!form.startDate && !!form.endDate;

  return (
    <>
      <PageHeader title="Session Years" description="Manage academic/session years across every school in your organization." />

      <div className="mb-6 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <h3 className="mb-3 text-sm font-semibold text-slate-800">
          {isEditing ? 'Edit Session Year' : 'Create Session Year'}
        </h3>
        <p className="mb-3 text-xs text-slate-500">
          {isEditing
            ? `Updates every school that already has "${form.oldName}".`
            : 'Creates this session year in every school in your organization at once — no school selection needed.'}
        </p>
        {formError && (
          <div className="mb-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{formError}</div>
        )}
        {resultSummary && (
          <div className="mb-3 rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-700">{resultSummary}</div>
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Field label="Name" required>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. 2026-27" />
          </Field>
          <Field label="Start Date" required>
            <Input type="date" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} />
          </Field>
          <Field label="End Date" required>
            <Input type="date" value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} />
          </Field>
        </div>

        <div className="mt-4 flex items-center gap-2">
          <button className="btn-primary" disabled={!canSubmit || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : isEditing ? 'Update' : 'Submit'}
          </button>
          {isEditing && (
            <button className="btn-secondary" onClick={() => setForm(BLANK_FORM)}>
              Cancel
            </button>
          )}
        </div>
      </div>

      <h3 className="mb-2 text-sm font-semibold text-slate-800">List Session Years</h3>
      <DataTable
        columns={COLUMNS}
        result={data}
        state={dt}
        loading={isFetching}
        emptyMessage="No session years yet."
        getRowId={(row) => row.name}
      />

      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={() => remove.mutate(deleting!.name)}
        title="Delete session year?"
        message={
          deleteError ??
          `This will delete "${deleting?.name}" from every school that isn't currently using it as their current year. This cannot be undone.`
        }
        confirmText="Delete"
        destructive
        loading={remove.isPending}
      />
    </>
  );
}
